import React, { useState } from "react";
import {
  Button,
  Col,
  Empty,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Switch,
  Typography
} from "antd";
import { DeleteOutlined, ImportOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import { centsToEuro, decimalInputProps, euroToCents, formatEuro } from "../workReports/format";
import { costTypeLabels } from "./labels";

/**
 * Cost lines of a case: imported from an offer or the final invoice (net cents per line), assigned
 * to the programme's measures, marked eligible or not. Edited as cards (phone friendly).
 */
const CostLines = ({ lines, measures, costs, sources, canEdit, canImport, onChange, onImport }) => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const [source, setSource] = useState(undefined);
  const types = costTypeLabels(t);

  const update = (index, patch) =>
    onChange(lines.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  const measureOptions = [
    { value: "", label: t("Not assigned") },
    ...measures.map(m => ({ value: m.key, label: m.label }))
  ];
  const sourceOptions = [
    ...(sources?.invoices || []).map(i => ({
      value: `invoice:${i._id}`,
      label: `${t("Invoice")} ${i.label}`
    })),
    ...(sources?.offers || []).map(o => ({
      value: `offer:${o._id}`,
      label: `${t("Offer")} ${o.label}`
    }))
  ];

  return (
    <div>
      {canImport && canEdit && (
        <Space.Compact className="fu-full mb-3">
          <Select
            className="fu-full"
            placeholder={t("Import costs from an offer or invoice")}
            value={source}
            onChange={setSource}
            options={sourceOptions}
            notFoundContent={t("No offers or invoices for this customer.")}
          />
          <Button
            icon={<ImportOutlined />}
            disabled={!source}
            onClick={() => {
              const [kind, id] = source.split(":");
              onImport(kind, id);
            }}
          >
            {t("Import")}
          </Button>
        </Space.Compact>
      )}

      {!lines.length && <Empty description={t("No cost lines yet.")} />}
      {lines.map((line, index) => (
        <div className="fu-line" key={index}>
          <Row gutter={[8, 8]} align="middle">
            <Col xs={24} lg={9}>
              <Input
                value={line.description}
                disabled={!canEdit}
                placeholder={t("Description")}
                onChange={event => update(index, { description: event.target.value })}
              />
            </Col>
            <Col xs={12} lg={4}>
              <InputNumber
                className="fu-full"
                value={centsToEuro(line.netCents)}
                disabled={!canEdit}
                precision={2}
                addonAfter={t("net")}
                {...decimalInputProps(lng)}
                onChange={value => update(index, { netCents: euroToCents(value) })}
              />
            </Col>
            <Col xs={12} lg={2}>
              <Select
                className="fu-full"
                value={line.taxRate}
                disabled={!canEdit}
                options={[19, 7, 0].map(rate => ({ value: rate, label: `${rate} %` }))}
                onChange={value => update(index, { taxRate: value })}
              />
            </Col>
            <Col xs={12} lg={3}>
              <Select
                className="fu-full"
                value={line.costType}
                disabled={!canEdit}
                options={Object.entries(types).map(([value, label]) => ({ value, label }))}
                onChange={value => update(index, { costType: value })}
              />
            </Col>
            <Col xs={12} lg={4}>
              <Select
                className="fu-full"
                value={line.measure || ""}
                disabled={!canEdit}
                options={measureOptions}
                status={line.eligible && !line.measure ? "warning" : undefined}
                onChange={value => update(index, { measure: value })}
              />
            </Col>
            <Col xs={24} lg={2}>
              <Space className="fu-line-actions">
                <Switch
                  size="small"
                  checked={line.eligible}
                  disabled={!canEdit}
                  onChange={checked => update(index, { eligible: checked })}
                />
                <span className="fu-small">{t("eligible")}</span>
                {canEdit && (
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    aria-label={t("Delete")}
                    onClick={() => onChange(lines.filter((_, i) => i !== index))}
                  />
                )}
              </Space>
            </Col>
          </Row>
        </div>
      ))}
      {canEdit && (
        <Button
          icon={<PlusOutlined />}
          onClick={() =>
            onChange([
              ...lines,
              {
                description: "",
                netCents: 0,
                taxRate: 19,
                costType: "material",
                measure: measures.length === 1 ? measures[0].key : "",
                eligible: true,
                source: "manual"
              }
            ])
          }
        >
          {t("Add line")}
        </Button>
      )}

      {costs?.byMeasure?.length > 0 && (
        <>
          <Typography.Title level={5} className="mt-4">
            {t("Costs per measure")}
          </Typography.Title>
          <ResponsiveTable
            size="small"
            rowKey="measure"
            pagination={false}
            mobileAvatar={false}
            dataSource={[
              ...costs.byMeasure,
              { ...costs.totals, measure: "__total", label: t("Total") }
            ]}
            rowClassName={row => (row.measure === "__total" ? "fu-total-row" : "")}
            columns={[
              {
                title: t("Measure"),
                key: "label",
                mobile: "title",
                render: (_, g) => g.label || t("Not assigned")
              },
              {
                title: t("Labour"),
                key: "labour",
                align: "right",
                render: (_, g) => formatEuro(g.labourNet, lng)
              },
              {
                title: t("Material"),
                key: "material",
                align: "right",
                render: (_, g) => formatEuro(g.materialNet, lng)
              },
              {
                title: t("Gross"),
                key: "gross",
                align: "right",
                render: (_, g) => formatEuro(g.gross, lng)
              },
              {
                title: t("Eligible"),
                key: "eligible",
                align: "right",
                render: (_, g) => formatEuro(g.eligible, lng)
              }
            ]}
          />
          <Typography.Paragraph type="secondary" className="fu-small mt-1">
            {costs.basis === "net"
              ? t("Eligible amounts are net (the applicant deducts input VAT).")
              : t("Eligible amounts are gross.")}
          </Typography.Paragraph>
        </>
      )}
    </div>
  );
};

export default CostLines;
